import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { surface, tone } from '../timeclock/tcFormat';
import { hours, kindColor, kindLabel, locale, parseDate, period, specialLabel, teamError, teamPost } from './teamFormat';
import { CheckList, KindChip } from './TeamParts';

/**
 * Waiting requests, first asked first served, each with the guidelines
 * checked again against today's calendar. A request with a red check can
 * only be approved as an exception, with a reason that is stored.
 */
const TeamApprovalsTab = ({ t, lang, theme, token, reloadKey, notify, failed, onChanged }) => {
    const [items, setItems] = useState(null);
    const [error, setError] = useState('');
    const [again, setAgain] = useState(0);
    const [open, setOpen] = useState({});   // id -> {mode: 'exception'|'reject', text}
    const [coordinated, setCoordinated] = useState({});
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        let alive = true;
        teamPost('queue', token).then((response) => {
            if (!alive) return;
            if (response?.success) {
                setItems(response.data || []);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, reloadKey, again, failed]);

    const decide = (item, approve, reason) => {
        setBusy(item.id);
        teamPost('decide', token, { id: item.id, approve, reason: reason || '', coordinated: !!coordinated[item.id] }).then((response) => {
            setBusy(null);
            if (response?.success) {
                notify(approve ? t('team_approved_mail', 'Approved; the colleague gets a mail.') : t('team_rejected_mail', 'Rejected; the colleague gets a mail.'), true);
                setOpen((o) => ({ ...o, [item.id]: undefined }));
                setAgain((k) => k + 1);
                if (onChanged) onChanged();
            } else if (response?.translation_key === 'team_reason_required') {
                setOpen((o) => ({ ...o, [item.id]: { mode: 'exception', text: o[item.id]?.text || '' } }));
                notify(teamError(t, response), false);
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    const s = surface(theme);

    if (error) return <Typography sx={{ color: tone.bad, fontSize: 13.5 }}>{error}</Typography>;
    if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;

    return (
        <Box sx={{ maxWidth: 900 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                {t('team_queue_note', 'Order = time of the request (first ask, first served). Leave of Tom, Jeroen and Kristof is not here: they plan it themselves.')}
            </Typography>
            {items.length === 0 && (
                <Box sx={{ ...s.card, p: 3 }}>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('team_queue_empty', 'No requests waiting.')}</Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {items.map((item) => {
                    const bad = (item.checks || []).some((c) => c.status === 'bad');
                    const groot = (item.checks || []).find((c) => c.key === 'groot');
                    const state = open[item.id];
                    return (
                        <Box key={item.id} sx={{ ...s.card, p: 2, borderLeft: '4px solid ' + (bad ? tone.bad : tone.ok) }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{item.staff?.name}</Typography>
                                <KindChip kind={item.kind} t={t} theme={theme} label={item.kind === 'SPECIAL' && item.special_code ? specialLabel(t, item.special_code) : undefined} />
                                <Typography sx={{ fontSize: 13.5 }}>
                                    {period(item.from, item.to, lang)}
                                    {item.days ? ' · ' + item.days.length + ' ' + (item.days.length === 1 ? t('team_day', 'day') : t('team_days', 'days')) : ''}
                                    {item.kind !== 'SPECIAL' && item.total ? ' · ' + hours(item.total, lang) : ''}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {t('team_asked', 'requested')} {item.created_at ? item.created_at.substring(8, 10) + '/' + item.created_at.substring(5, 7) + ' ' + item.created_at.substring(11, 16) : ''}
                                </Typography>
                            </Box>
                            {item.remark && <Typography sx={{ fontSize: 13.5, mb: 1.25, whiteSpace: 'pre-line' }}>"{item.remark}"</Typography>}
                            {item.context && item.context.rows.length > 0 && (
                                <ContextGrid context={item.context} t={t} lang={lang} theme={theme} />
                            )}
                            <CheckList t={t} lang={lang} checks={item.checks} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                {groot && !groot.params?.coordinated && (
                                    <FormControlLabel sx={{ mr: 1 }} control={
                                        <Checkbox size="small" checked={!!coordinated[item.id]} onChange={(e) => setCoordinated((c) => ({ ...c, [item.id]: e.target.checked }))} />
                                    } label={<Typography sx={{ fontSize: 13.5 }}>{t('team_coordinated', 'coordinated with the department')}</Typography>} />
                                )}
                                <Box sx={{ flex: 1 }} />
                                {!state && (
                                    <>
                                        <Button size="small" color="error" variant="outlined" disabled={busy === item.id}
                                            onClick={() => setOpen((o) => ({ ...o, [item.id]: { mode: 'reject', text: '' } }))} sx={{ textTransform: 'none' }}>
                                            {t('team_reject', 'Reject')}
                                        </Button>
                                        <Button size="small" variant={bad ? 'outlined' : 'contained'} disableElevation disabled={busy === item.id}
                                            onClick={() => (bad && !(groot && coordinated[item.id] && (item.checks || []).filter((c) => c.status === 'bad').length === 1 && groot.status === 'bad')
                                                ? setOpen((o) => ({ ...o, [item.id]: { mode: 'exception', text: '' } }))
                                                : decide(item, true, ''))}
                                            sx={{ textTransform: 'none' }}>
                                            {bad ? t('team_allow_exception', 'Allow an exception…') : t('team_approve', 'Approve')}
                                        </Button>
                                    </>
                                )}
                            </Box>
                            {state && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <TextField size="small" autoFocus sx={{ flex: '1 1 300px' }} value={state.text}
                                        onChange={(e) => setOpen((o) => ({ ...o, [item.id]: { ...state, text: e.target.value } }))}
                                        label={state.mode === 'reject' ? t('team_reject_reason', 'Reason (optional, the colleague sees it)') : t('team_exception_reason', 'Reason for the exception')}
                                        placeholder={state.mode === 'reject' ? '' : t('team_exception_example', 'e.g. installation moved, Siemen takes over')} />
                                    <Button size="small" variant="contained" disableElevation color={state.mode === 'reject' ? 'error' : 'primary'}
                                        disabled={busy === item.id || (state.mode === 'exception' && !state.text.trim())}
                                        onClick={() => decide(item, state.mode !== 'reject', state.text)} sx={{ textTransform: 'none' }}>
                                        {state.mode === 'reject' ? t('team_reject', 'Reject') : t('team_approve_exception', 'Approve as exception')}
                                    </Button>
                                    <Button size="small" onClick={() => setOpen((o) => ({ ...o, [item.id]: undefined }))} sx={{ textTransform: 'none' }}>{t('team_back', 'Back')}</Button>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TeamApprovalsTab;

/**
 * The colleagues this request matters for, on the requested days, in the
 * same blocks as the calendar; the requester's row shows this request.
 * Below: how many are still present if it is approved.
 */
const ContextGrid = ({ context, t, lang, theme }) => {
    const s = surface(theme);
    const dark = theme === 'dark';
    const loc = locale(lang);
    const hatch = 'repeating-linear-gradient(135deg, transparent 0 4px, ' + (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)') + ' 4px 5px)';
    const others = context.rows.length - 1;
    const groupLabel = {
        DEV: t('team_ctx_dev', 'Development (Bert, Mathias and Kristof)'),
        SUPPORT: t('team_ctx_support', 'Support'),
        OFFICE: t('team_ctx_office', 'Administration and marketing'),
    }[context.group] || context.group;
    const presentColor = (n) => {
        if (context.group === 'DEV') return n < 1 ? tone.bad : tone.ok;
        if (context.group === 'SUPPORT') return others - n >= 2 ? tone.bad : tone.ok;
        return 'text.primary';
    };
    const W = context.days.length > 6 ? 46 : 72;

    const cell = (row, day) => {
        const c = row.days[day] || {};
        const e = (c.entries || [])[0];
        let background = c.off ? hatch : 'transparent';
        let border = 'none';
        let text = '';
        const tip = [];
        if (e) {
            const color = kindColor(e.kind, theme);
            const pending = e.status === 'PENDING';
            background = pending ? 'repeating-linear-gradient(135deg, ' + color + (e.this ? 'AA' : '55') + ' 0 4px, transparent 4px 8px)' : color;
            border = pending ? (e.this ? '2px solid ' : '1.5px solid ') + color : 'none';
            (c.entries || []).forEach((x) => {
                tip.push((x.kind === 'SPECIAL' && x.special_code ? specialLabel(t, x.special_code) : kindLabel(t, x.kind))
                    + (x.kind !== 'SPECIAL' ? ' · ' + hours(x.hours, lang) : '')
                    + (x.this ? ' · ' + t('team_ctx_this', 'this request') : x.status === 'PENDING' ? ' · ' + t('team_status_pending', 'requested') : ''));
            });
        } else if (c.off) {
            tip.push(t('team_legend_off', 'Day off by roster'));
        } else {
            text = c.home ? '⌂' : '';
            tip.push(c.home ? t('team_home', 'Works from home') : t('team_ctx_present', 'present'));
        }
        return (
            <Tooltip key={day} disableInteractive title={<Box sx={{ fontSize: 12.5 }}><b>{row.staff.name}</b>{tip.map((x, i) => <div key={i}>{x}</div>)}</Box>}>
                <Box sx={{ width: W, height: 26, flexShrink: 0, p: '3px', boxSizing: 'border-box', borderLeft: s.line }}>
                    <Box sx={{ width: '100%', height: '100%', borderRadius: '4px', background, border, boxSizing: 'border-box',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: s.faint }}>{text}</Box>
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: s.faint, mb: 0.5 }}>
                {groupLabel}
            </Typography>
            <Box sx={{ border: s.line, borderRadius: '6px', overflowX: 'auto', display: 'inline-block', maxWidth: '100%' }}>
                <Box sx={{ display: 'flex', borderBottom: s.line }}>
                    <Box sx={{ width: 120, flexShrink: 0 }} />
                    {context.days.map((day) => (
                        <Box key={day} sx={{ width: W, flexShrink: 0, textAlign: 'center', fontSize: 11.5, py: 0.4, borderLeft: s.line, color: 'text.secondary', lineHeight: 1.25 }}>
                            <Box sx={{ textTransform: 'capitalize' }}>{parseDate(day).toLocaleDateString(loc, { weekday: 'short' })}</Box>
                            <Box sx={{ fontWeight: 600, color: 'text.primary' }}>{parseDate(day).toLocaleDateString(loc, { day: 'numeric', month: 'short' })}</Box>
                        </Box>
                    ))}
                </Box>
                {context.rows.map((row) => (
                    <Box key={row.staff.id} sx={{ display: 'flex', alignItems: 'center', borderTop: s.line }}>
                        <Box sx={{ width: 120, flexShrink: 0, px: 1, fontSize: 13, fontWeight: row.requester ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.staff.name}
                        </Box>
                        {context.days.map((day) => cell(row, day))}
                    </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', borderTop: s.line, backgroundColor: dark ? 'rgba(255,255,255,0.03)' : '#FAFBFC' }}>
                    <Box sx={{ width: 120, flexShrink: 0, px: 1, fontSize: 11.5, color: s.faint, lineHeight: 1.2 }}>{t('team_ctx_present_if', 'Present if approved')}</Box>
                    {context.days.map((day) => (
                        <Box key={day} sx={{ width: W, flexShrink: 0, textAlign: 'center', fontSize: 12.5, fontWeight: 700, py: 0.5, borderLeft: s.line,
                            color: presentColor(context.present[day]) }}>{context.present[day]}</Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
